import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { DocumentCategory, DocumentCategoryFormValues } from "../models/documentCategory"
import { documentCategoryApi } from "../services/documentCategoryApi"

const BASE_KEY = "document-categories"

export function useAllCategories() {
  return useQuery<DocumentCategory[]>({
    queryKey: [BASE_KEY, "all"],
    queryFn: async () => {
      const data = await documentCategoryApi.getAll()
      return Array.isArray(data) ? data : []
    },
    staleTime: 1000 * 60 * 2,
  })
}

export function useCategoryHierarchy(excludeId?: number) {
  return useQuery<{ id: string; name: string }[]>({
    queryKey: [BASE_KEY, "hierarchy", excludeId],
    queryFn: async () => {
      const data = await documentCategoryApi.getHierarchy(excludeId)
      return Array.isArray(data) ? data : []
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useCreateDocumentCategory() {
  const queryClient = useQueryClient()
  return useMutation<DocumentCategory, Error, DocumentCategoryFormValues>({
    mutationFn: (values) => documentCategoryApi.create(values) as Promise<DocumentCategory>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, "hierarchy"] })
    },
  })
}

export function useUpdateDocumentCategory() {
  const queryClient = useQueryClient()
  return useMutation<DocumentCategory, Error, { id: number; values: Partial<DocumentCategoryFormValues> }>({
    mutationFn: ({ id, values }) => documentCategoryApi.update(id, values) as Promise<DocumentCategory>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, "hierarchy"] })
    },
  })
}

export function useDeleteDocumentCategory() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => documentCategoryApi.delete(id) as Promise<void>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, "hierarchy"] })
    },
  })
}

export function useReorderDocumentCategories() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { id: number; root: number; order: number }[]>({
    mutationFn: (items) => documentCategoryApi.reorder(items) as Promise<void>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
      queryClient.invalidateQueries({ queryKey: [BASE_KEY, "hierarchy"] })
    },
  })
}
